import React, { useState, useEffect } from 'react';
import { get, set } from 'global/lib/lodash';
import { CenterContent, GridContainer } from 'global/components/perform-grid';
import { Box, Icon, Checkbox, FormControlLabel } from '@mui/material';
import { withRouter } from 'react-router-dom';
import {
  ADMIN_FORM_STYLE,
  ADMIN_SEARCH_BAR,
  AdminContainerManage,
  AdminContentRoot,
} from './styles';
import { Row } from 'global/components/row';
import { Text } from 'global/components/text';
import { Button } from 'global/components/Button';
import Form from 'global/components/form';
import { DocumentTypes } from './editions';
import { _size, uppercaseFirst, cleanCopy, flattenAllChildren, waitIdle } from 'global/utils';
import sendData from 'redux/actions/sendData';
import SearchBar from 'global/components/SearchBar';
import { FlexGridDataTable, FlexGridRow, renderFlexGridColumns } from 'global/components/flex-grid-list';
import SnackAlerts from 'global/components/snack-alerts';
import useDebounce from 'global/hooks/useDebounce';

const EDITION_DOCUMENT_BATCH_SIZE = 5;

export const CloneEdition = withRouter((props) => {
  const { doc } = props;
  const { editionDocuments } = doc;

  const [state, setState] = useState({
    isEdit: true,
    isEditList: true,
    formLoading: false,
    search: '',
    totalCount: 0,
    allItems: [],
    items: [],
  });

  useEffect(() => {
    if (props.open) {
      const flat = flattenAllChildren(editionDocuments);

      const allItems = (cleanCopy(editionDocuments) || []).map((c) => ({ ...c, children: get(c, 'children', []).map((child) => ({ ...child, included: true })) }));

      setState((s) => ({
        ...s,
        forceUpdate: 0,
        totalCount: _size(flat),
        allItems: allItems,
        items: allItems,
      }));
    }
  }, [props.open]);

  const [formState, setFormState] = useState({});
  const [alerts, setAlerts] = useState();

  const data = doc;

  const debSearch = useDebounce(state.search, 700);

  useEffect(() => {
    if (!!debSearch) {
      const st = debSearch.toLowerCase();
      const docs = cleanCopy(state.allItems).filter((d) => {
        const sels = ['document.name'];

        if (d.children) {
          d.children = d.children.filter((dc) => {
            let found;

            if (sels.find((s) => get(d, s, '').toLowerCase().indexOf(st) > -1)) {
              found = true;
            }

            return found;
          });
        }

        return !!_size(d.children);
      });
      setState((s) => ({ ...s, items: docs }));
    } else {
      setState((s) => ({ ...s, items: s.allItems }));
    }
  }, [debSearch]);

  const close = () => {
    props.onClose && props.onClose();
  };

  const onSave = async () => {
    try {
      setState((s) => ({ ...s, formLoading: true }));

      const [edition] = await sendData(
        {
          url: `/editions`,
          method: 'post',
          data: {
            data: {
              type: 'editions',
              attributes: {
                name: get(formState, 'name'),
                edition_type: get(formState, 'edition_type'),
                realm_id: get(doc, 'realm.id'),
                status: 'draft',
                parent_id: get(formState, 'parent.selected') ? doc.id : null,
                config: {
                  max_score: get(doc,'config.maxScore',1),
                  min_score: get(doc,'config.minScore',0),
                  display_percentage: get(doc,'config.displayPercentage',true),
                },
              },
              relationships: {
                realms: doc.realms ? { data: doc.realms.map(r => ({ type: 'realms', id: `${r.id}` })) } : undefined,
                userGroups: doc.userGroups ? { data: doc.userGroups.map(r => ({ type: 'user-groups', id: `${r.id}` })) } : undefined,
              }
            },
          },
        },
        {
          normalize: {
            build: ['editions'],
          },
        }
      );

      const childrenToClone = state.items.flatMap((item) => item.children.filter((child) => !!child.included));

      // Post a few at a time. sendData retries transient failures before it
      // settles, and the next batch waits until this one has fully settled.
      for (let start = 0; start < childrenToClone.length; start += EDITION_DOCUMENT_BATCH_SIZE) {
        const batch = childrenToClone.slice(start, start + EDITION_DOCUMENT_BATCH_SIZE);
        const results = await Promise.allSettled(
          batch.map((child, batchIndex) =>
            sendData({
              url: `/edition-documents`,
              method: 'post',
              data: {
                data: {
                  type: 'edition-documents',
                  attributes: {
                    order: start + batchIndex + 1,
                    edition_id: get(edition, 'id'),
                    documentable_type: child.documentableType,
                    meta: child.meta,
                    shared: child.shared,
                    primary_document: child.primaryDocument,
                    documentable_id: child.documentableId,
                    classification_id: child.classificationId,
                  },
                },
              },
            })
          )
        );

        const failed = results.find((result) => result.status === 'rejected');
        if (failed) {
          throw failed.reason;
        }
      }

      close(true);
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, formLoading: false }));
    }
  };

  const toggleAll = async (checked) => {
    const setAllTo = (val) => {
      setState((s) => ({
        ...s,
        items: s.items.map((item) => ({ ...item, children: item.children.map((child) => ({ ...child, included: val })) })),
      }));
    };

    await waitIdle();

    if (!checked) {
      //turn all off
      setAllTo(false);
    } else {
      setAllTo(true);
    }
  };

  const renderRow = (row) => {
    return (
      <Box key={row.id} sx={{ width: '100%' }}>
        <Row fullWidth>
          {state.isEditList && <Icon>drag_indicator</Icon>}
          <Text style={{ paddingLeft: 8 }}>{get(row, 'document.name')}</Text>
          <Text italic>{`: ${get(row, 'document.classification.name', get(row, 'classification.name'))}`}</Text>
        </Row>
        {(row.children || []).map((item, i) => (
          <FlexGridRow key={item.id} layout="columns" dense>
            {renderFlexGridColumns(COLUMNS, item, i)}
          </FlexGridRow>
        ))}
      </Box>
    );
  };

  const COLUMNS = [
    {
      field: 'blank',
      title: '',
      width: 20,
      render: (row) => {
        return (
          <Checkbox
            className="offsetCheck"
            checked={!!get(row, 'included')}
            onChange={(e) => {
              set(row, 'included', e.target.checked);
              setState((s) => ({ ...s, forceUpdate: s.forceUpdate + 1 }));
            }}
            size="small"
          />
        );
      },
    },
    {
      field: 'document.name',
      title: 'Title',
      width: 200,
    },
    {
      field: 'documentableType',
      title: 'Type',
      width: 200,
      render: (row, text) => {
        return uppercaseFirst(text);
      },
    },
    {
      field: 'shared',
      title: 'Shared',
      width: 100,
      render: (row, text) => {
        return !!row.shared ? 'Shared' : 'Unique';
      },
    },
    {
      field: 'primary',
      title: 'Primary',
      width: 100,
      render: (row, text, index) => {
        return !!row.primaryDocument ? 'Yes' : 'No';
      },
    },
  ];

  let checked = 0;
  state.items.map((item) => {
    item.children.map((child) => {
      if (child.included) {
        checked += 1;
      }
    });
  });
  const allChecked = checked >= state.totalCount;

  return (
    <GridContainer>
      <CenterContent fullWidth>
        <AdminContentRoot>
          <AdminContainerManage>
          <div className="mainContainer">
            <Form
              showAllScreens
              formLoading={state.formLoading}
              onFormState={setFormState}
              formStyle={ADMIN_FORM_STYLE}
              screens={[
                {
                  inputs: [
                    {
                      id: 'edition_type',
                      type: 'Select',
                      data: DocumentTypes,
                      defaultValue: () => get(data, 'editionType', ''),
                      props: {
                        label: 'Type',
                        disabled: !state.isEdit,
                      },
                    },
                    {
                      id: 'name',
                      type: 'TextField',
                      defaultValue: () => `${get(data, 'name', '')} (Copy)`,
                      props: {
                        label: 'Title',
                        disabled: !state.isEdit,
                      },
                    },
                    {
                      id: 'parent',
                      type: 'Checkbox',
                      options: [{ value: 'selected', label: 'This Edition is a new version of the previous' }],
                      defaultValue: () => ({ selected: true }),
                      props: {
                        label: 'Parent',
                        tooltip: {
                          title: (
                            <div>
                              This setting determines if the old Edition is set as a parent of the new one. If they are tied together this way, the system treats the new Edition as if it's the latest
                              version of the previous when comparing score data.
                              <br />
                              <br />
                              <b>Leave this checked if...</b> data collected using this new content should be compared alongside the old.
                              <br />
                              <br />
                              <b>Uncheck this if...</b> the new Edition represents a separate dataset from that of the previous
                            </div>
                          ),
                        },
                      },
                    },
                  ],
                },
              ]}
            />

            <Row>
              <Text type="headline_large">{'Document List'}</Text>
            </Row>

            <SearchBar containerStyle={ADMIN_SEARCH_BAR} changeSearch={(t) => setState((s) => ({ ...s, search: t }))} />

            <div style={{ width: '100%' }}>
              <Row>
                <FormControlLabel control={<Checkbox checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />} label={`${checked} items to be cloned`} />

                <Row style={{ paddingBottom: 8, marginLeft: 'auto' }} center>
                  <Button style={{ marginRight: 8 }} variant="text" onClick={close} startIcon={<Icon>close</Icon>}>
                    CANCEL
                  </Button>
                  <Button onClick={() => onSave()} startIcon={<Icon>save</Icon>} color="secondary">
                    CLONE
                  </Button>
                </Row>
              </Row>
              <FlexGridDataTable
                columns={COLUMNS}
                sortable
                canDrag={state.isEditList}
                onOrderChange={(items) => setState((s) => ({ ...s, items }))}
                items={state.items}
                renderRow={renderRow}
              />
            </div>
          </div>
          </AdminContainerManage>
        </AdminContentRoot>

        <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
      </CenterContent>
    </GridContainer>
  );
});
